import clsx from 'clsx';

import type * as api from '@gitbook/api-client';
import type { EvaluateMergeRuleResult, EvaluateMergeRulesResult } from '@gitbook/app-merge-rules';
import { intlPluralizeString } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    type BadgeKind,
    Button,
    Description,
    Disclosure,
    DisclosureChevron,
    DisclosurePanel,
    DisclosureTrigger,
    type IconDefinition,
    Label,
    LabeledContainer,
    LabeledContainerActionableTitle,
    LabeledContainerBody,
    LabeledContainerHeader,
    List,
    ListItem,
    ListItemBody,
    ListItemDescription,
    ListItemIndicator,
    type ListRenderItemInfo,
    Popover,
    PopoverRoot,
    PopoverTrigger,
} from '@gitbook/spine-react';

import { useEvaluateMergeRules, useSpaceMergeRules } from '../MergeRules';

type ChangeRequestMergeRulesStatusProps = {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    mergeRulesEvaluation: EvaluateMergeRulesResult;
};

/**
 * Button to open the merge rules status popover
 */
export function ChangeRequestMergeRulesStatusButton(props: ChangeRequestMergeRulesStatusProps) {
    return (
        <PopoverRoot>
            <PopoverTrigger>
                <Button
                    label="Merging is blocked"
                    tooltip={{
                        label: 'Merge is blocked by merge rules',
                        description: 'Click to review the merge rules that are blocking the merge',
                    }}
                    kind="secondary"
                    size="medium"
                    trailingIcon={Icon.MergeRulesBlock}
                    iconOnly
                />
            </PopoverTrigger>
            <Popover placement="bottom-end" className="w-96 p-4">
                <ChangeRequestMergeRulesStatusWithData {...props} />
            </Popover>
        </PopoverRoot>
    );
}

/**
 * Fetch and summarize the merge rules status.
 */
export function ChangeRequestMergeRulesStatus(
    props: Omit<ChangeRequestMergeRulesStatusProps, 'mergeRulesEvaluation'>
) {
    const { space, changeRequest } = props;
    const mergeRulesEvaluation = useEvaluateMergeRules({ space, changeRequest });

    return (
        <ChangeRequestMergeRulesStatusWithData
            {...props}
            mergeRulesEvaluation={mergeRulesEvaluation}
        />
    );
}

/**
 * Summarize the merge rules status.
 */
function ChangeRequestMergeRulesStatusWithData(props: ChangeRequestMergeRulesStatusProps) {
    const { space, changeRequest, mergeRulesEvaluation } = props;
    const mergeRules = useSpaceMergeRules(space);
    const status = getMergeRulesStatusDescription({ mergeRules, mergeRulesEvaluation });

    return (
        <LabeledContainer>
            <LabeledContainerHeader>
                <LabeledContainerActionableTitle>
                    <Badge size="small" iconOnly icon={status.icon} kind={status.badgeKind} />
                    <Label className={clsx('flex-1')}>
                        <span className={status.labelColor}>{status.label}</span>
                    </Label>
                    {space.permissions.admin ? (
                        <Button
                            label="Configure merge rules for this space"
                            iconOnly
                            kind="blank"
                            leadingIcon={Icon.Settings}
                            href={{
                                screen: {
                                    type: 'space',
                                    space: space.id,
                                    organization: space.organization,
                                    changeRequest: changeRequest.number,
                                    sideSheet: { type: 'merge-rule' },
                                },
                            }}
                        />
                    ) : null}
                </LabeledContainerActionableTitle>
                <Description>{status.description}</Description>
            </LabeledContainerHeader>
            {mergeRulesEvaluation.rejectedRules.length > 0 ||
            mergeRulesEvaluation.passedRules.length > 0 ||
            mergeRulesEvaluation.skippedRules.length > 0 ? (
                <LabeledContainerBody>
                    {mergeRulesEvaluation.rejectedRules.length > 0 ? (
                        <ChangeRequestMergeRulesList
                            title={`${mergeRulesEvaluation.rejectedRules.length} failing ${intlPluralizeString(
                                {
                                    count: mergeRulesEvaluation.rejectedRules.length,
                                    singular: 'rule',
                                    plural: 'rules',
                                }
                            )}`}
                            status="rejected"
                            rules={mergeRulesEvaluation.rejectedRules}
                            defaultExpanded
                        />
                    ) : null}
                    {mergeRulesEvaluation.passedRules.length > 0 ? (
                        <ChangeRequestMergeRulesList
                            title={`${mergeRulesEvaluation.passedRules.length} passing ${intlPluralizeString(
                                {
                                    count: mergeRulesEvaluation.passedRules.length,
                                    singular: 'rule',
                                    plural: 'rules',
                                }
                            )}`}
                            status="passed"
                            rules={mergeRulesEvaluation.passedRules}
                            defaultExpanded={false}
                        />
                    ) : null}
                    {mergeRulesEvaluation.skippedRules.length > 0 ? (
                        <ChangeRequestMergeRulesList
                            title={`${mergeRulesEvaluation.skippedRules.length} skipped ${intlPluralizeString(
                                {
                                    count: mergeRulesEvaluation.skippedRules.length,
                                    singular: 'rule',
                                    plural: 'rules',
                                }
                            )}`}
                            status="skipped"
                            rules={mergeRulesEvaluation.skippedRules}
                            defaultExpanded={false}
                        />
                    ) : null}
                </LabeledContainerBody>
            ) : null}
        </LabeledContainer>
    );
}

function ChangeRequestMergeRulesList(props: {
    title: string;
    status: 'rejected' | 'passed' | 'skipped';
    rules: EvaluateMergeRuleResult[];
    defaultExpanded?: boolean;
}) {
    const { title, status, rules, defaultExpanded } = props;

    return (
        <Disclosure defaultExpanded={defaultExpanded}>
            {({ isExpanded }) => (
                <div className={clsx('flex flex-col', isExpanded ? 'gap-1' : null)}>
                    <div className="-ml-1.5">
                        <DisclosureTrigger>
                            <Button
                                trailingIcon={<DisclosureChevron />}
                                kind="blank"
                                size="small"
                                label={title}
                            />
                        </DisclosureTrigger>
                    </div>
                    <DisclosurePanel className="flex flex-col">
                        <List
                            items={rules}
                            renderItem={({ item }: ListRenderItemInfo<EvaluateMergeRuleResult>) => (
                                <ListItem compact>
                                    <ListItemIndicator>
                                        <Badge
                                            size="xsmall"
                                            kind={
                                                status === 'rejected'
                                                    ? 'danger'
                                                    : status === 'skipped'
                                                      ? 'secondary'
                                                      : 'success'
                                            }
                                            iconOnly
                                            icon={
                                                status === 'rejected'
                                                    ? Icon.X
                                                    : status === 'skipped'
                                                      ? Icon.Minus
                                                      : Icon.Check
                                            }
                                        />
                                    </ListItemIndicator>
                                    <ListItemBody>
                                        <ListItemDescription
                                            className={status === 'skipped' ? 'opacity-60' : ''}
                                        >
                                            {item.message}
                                        </ListItemDescription>
                                    </ListItemBody>
                                </ListItem>
                            )}
                        />
                    </DisclosurePanel>
                </div>
            )}
        </Disclosure>
    );
}

function getMergeRulesStatusDescription({
    mergeRules,
    mergeRulesEvaluation,
}: {
    mergeRules: api.MergeRulesStandaloneConfiguration;
    mergeRulesEvaluation: EvaluateMergeRulesResult;
}): {
    icon: IconDefinition;
    badgeKind: BadgeKind;
    label: string;
    labelColor?: string;
    description: string;
} {
    if (mergeRulesEvaluation.result === 'rejected') {
        return {
            icon: Icon.MergeRulesBlock,
            badgeKind: 'danger',
            label: 'Merging is blocked',
            labelColor: 'text-danger',
            description: 'Merging is blocked by merge rules.',
        };
    }

    if (mergeRules.type === 'none') {
        return {
            icon: Icon.MergeRulesAllow,
            badgeKind: 'secondary',
            label: 'Merging is allowed',
            description: 'No merge rules are configured.',
        };
    }

    return {
        icon: Icon.MergeRulesAllow,
        badgeKind: 'success',
        label: 'Merging is allowed',
        labelColor: 'text-success',
        description: 'Merge rules are passed.',
    };
}
